/**
 * Chart tokens + pure geometry helpers for the telemetry detail charts.
 * Colors track the telemetry palette used elsewhere (rate = sky, errors = rose,
 * latency = violet) so the charts read the same as the edge-health overlay.
 * No JSX / no React here — pure so the geometry is unit-testable.
 */

/** Metric accent colors (fill gradients derive from these). */
export const CHART_COLORS = {
  rate: "#38bdf8", // sky-400
  errors: "#f43f5e", // rose-500
  p50: "#c4b5fd", // violet-300 (faint inner line)
  p95: "#a78bfa", // violet-400 (band edge)
  p99: "#f0abfc", // fuchsia-300 (tail line)
  band: "#8b5cf6", // violet-500 (band fill)
  axis: "#334155", // slate-700 (baseline / grid)
  crosshair: "#94a3b8", // slate-400
} as const;

/** Inner drawing margins — leave room for the crosshair dot + top stroke. */
export const CHART_MARGIN = { top: 4, right: 3, bottom: 3, left: 3 } as const;

/** Default chart height in the details panel (compact but readable). */
export const CHART_HEIGHT = 44;

/**
 * A "nice" upper bound for a y-domain: the max padded ~12% then rounded up to a
 * pleasant step, so the area never touches the ceiling and the top gridline sits
 * on a round number. Always ≥ a tiny epsilon so a flat-zero series still has a
 * non-degenerate domain.
 */
const NICE_STEPS = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

export function niceTop(max: number): number {
  if (!(max > 0)) return 1;
  const padded = max * 1.12;
  const mag = Math.pow(10, Math.floor(Math.log10(padded)));
  const norm = padded / mag; // [1, 10)
  // Finer ladder than {1,2,5,10} so a mid value (e.g. 5.4) doesn't jump to 10×
  // and squash the plot — these axis-less mini-charts want the vertical space.
  const step = NICE_STEPS.find((s) => norm <= s) ?? 10;
  return step * mag;
}

/** X pixel for point `i` of `count` across an inner width (evenly spaced). */
export function xAt(i: number, count: number, innerWidth: number, left: number): number {
  if (count <= 1) return left;
  return left + (i / (count - 1)) * innerWidth;
}

/** Nearest point index for a pixel x within the inner plot. */
export function indexAtX(x: number, count: number, innerWidth: number, left: number): number {
  if (count <= 1 || !Number.isFinite(x)) return 0;
  const t = (x - left) / (innerWidth || 1);
  return Math.max(0, Math.min(count - 1, Math.round(t * (count - 1))));
}
